/**
 * The inbox, scoped to whichever workspace is open.
 */
import type {
  MarkNotificationsReadRequest,
  NotificationsResponse,
} from "@ondo/shared";

import { request } from "./request.ts";

/**
 * The inbox: everything addressed to this account.
 *
 * Takes no id and can take none — the server derives the feed from the session,
 * for the reason `fetchMyActivity` gives. Reading does NOT mark anything seen,
 * so the sidebar badge can call this on every page without clearing itself; the
 * inbox screen says so separately with `markNotificationsSeen`.
 *
 * An empty list is a real answer three times over: nothing has been sent, the
 * account has no principal yet, or the feed store is not configured for this
 * deployment. None of them is a failure.
 */
export const fetchNotifications = (principalOndoId?: string | null) =>
  request<NotificationsResponse>(`/notifications${inboxQuery(principalOndoId)}`);

/** Everything currently in the inbox stops being new. Called when the screen opens. */
export const markNotificationsSeen = (principalOndoId?: string | null) =>
  request<void>(`/notifications/seen${inboxQuery(principalOndoId)}`, {});

/**
 * `?principal=ONDO-1024`, or nothing at all on a personal workspace.
 *
 * WHOSE inbox is a fact about the open workspace, not about the reader: a
 * gallery is told a settlement fell due at the FIRM's ONDO id, and no person's
 * feed carries it. The server verifies the id against `principals_read` rather
 * than trusting it, so this is addressing and not authorization — the same
 * shape `fetchVerificationRequests` already uses.
 */
function inboxQuery(principalOndoId?: string | null): string {
  return principalOndoId ? `?principal=${encodeURIComponent(principalOndoId)}` : "";
}

/**
 * Marks specific notifications read.
 *
 * The ids are Stream group ids, taken from `NotificationEntry.id` and passed
 * back unchanged. They are applied to the caller's own feed, so one belonging to
 * somebody else's inbox marks nothing.
 */
export const markNotificationsRead = (
  body: MarkNotificationsReadRequest,
  principalOndoId?: string | null,
) => request<void>(`/notifications/read${inboxQuery(principalOndoId)}`, body);
